import BaseApi, { Response, PagedResponse } from './baseApi';
import {
  BaseAssessmentExchange,
  CreateAssessmentExchange,
  UpdateAssessmentExchange,
} from '../dtos/AssessmentExchanges/index';

class AssessmentExchangeApi extends BaseApi {
  private readonly basePath = '/assessment-exchanges';

  /**
   * Get assessment exchange by ID
   * GET /api/assessment-exchanges/{id}
   */
  async getById(id: string): Promise<Response<BaseAssessmentExchange | null>> {
    return this.get<BaseAssessmentExchange | null>(`${this.basePath}/${id}`);
  }

  /**
   * Get all assessment exchanges
   * GET /api/assessment-exchanges
   */
  async getList(): Promise<Response<BaseAssessmentExchange[]>> {
    return this.get<BaseAssessmentExchange[]>(this.basePath);
  }

  /**
   * Get assessment exchanges by clinical session ID
   * GET /api/assessment-exchanges/clinical-session/{clinicalSessionId}
   */
  async getByClinicalSessionId(clinicalSessionId: string): Promise<Response<BaseAssessmentExchange[]>> {
    return this.get<BaseAssessmentExchange[]>(`${this.basePath}/clinical-session/${clinicalSessionId}`);
  }

  /**
   * Get paginated list of assessment exchanges with optional search
   * GET /api/assessment-exchanges/search?searchTerm=&pageNumber=1&pageSize=10 (when search is provided)
   * GET /api/assessment-exchanges/paged?pageNumber=1&pageSize=10 (when no search)
   * Searches: AssessmentNameSnapshot, AssessmentSpecializationSnapshot
   */
  async getPagedList(
    pageNumber: number = 1,
    pageSize: number = 10,
    searchTerm?: string
  ): Promise<Response<PagedResponse<BaseAssessmentExchange>>> {
    if (searchTerm) {
      const params = new URLSearchParams();
      params.append('searchTerm', searchTerm);
      params.append('pageNumber', pageNumber.toString());
      params.append('pageSize', pageSize.toString());
      return this.get<PagedResponse<BaseAssessmentExchange>>(
        `${this.basePath}/search?${params.toString()}`
      );
    }
    
    const params = new URLSearchParams({
      pageNumber: pageNumber.toString(),
      pageSize: pageSize.toString(),
    });
    return this.get<PagedResponse<BaseAssessmentExchange>>(
      `${this.basePath}/paged?${params.toString()}`
    );
  }

  /**
   * Create a new assessment exchange
   * POST /api/assessment-exchanges
   */
  async create(data: CreateAssessmentExchange): Promise<Response<BaseAssessmentExchange>> {
    return this.post<BaseAssessmentExchange, CreateAssessmentExchange>(this.basePath, data);
  }

  /**
   * Update an existing assessment exchange
   * PUT /api/assessment-exchanges/{id}
   */
  async update(id: string, data: Omit<UpdateAssessmentExchange, 'id'>): Promise<Response<BaseAssessmentExchange>> {
    return this.put<BaseAssessmentExchange, UpdateAssessmentExchange>(`${this.basePath}/${id}`, { ...data, id });
  }

  /**
   * Delete an assessment exchange
   * DELETE /api/assessment-exchanges/{id}
   */
  async deleteAssessmentExchange(id: string): Promise<Response<boolean>> {
    return super.delete<boolean>(`${this.basePath}/${id}`);
  }
}

// Export singleton instance
export const assessmentExchangeApi = new AssessmentExchangeApi();

export default AssessmentExchangeApi;
